import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import CVProposalDiff from "@/components/CVProposalDiff";
import {
  Upload, FileText, Loader2, ShieldCheck, CheckCircle2, AlertCircle, Wand2, Target,
} from "lucide-react";

const SECTION_LABELS = {
  work_experiences: "Arbetslivserfarenhet",
  educations: "Utbildning",
  skills: "Kompetenser",
  systems_tools: "System och verktyg",
  contact: "Kontaktuppgifter",
};

export default function ImporteraCV() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null); // {import_id, extracted, gaps}
  const [selected, setSelected] = useState({});
  const [buildMode, setBuildMode] = useState(null); // "improve" | "adapt"
  const [jobAd, setJobAd] = useState("");
  const [building, setBuilding] = useState(false);
  const [proposal, setProposal] = useState(null); // built cv doc

  const buildCv = async (mode) => {
    if (mode === "adapt" && !jobAd.trim()) { toast.error("Klistra in jobbannonsen för att anpassa CV:t"); return; }
    setBuilding(true);
    setBuildMode(mode);
    try {
      const { data } = await api.post(`/cv-import/${result.import_id}/build-cv`, {
        mode, job_ad_text: mode === "adapt" ? jobAd : "", template: "modern",
      });
      setProposal(data);
      toast.success("Förslag skapat – jämför original och förslag nedan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBuildMode(null);
    } finally { setBuilding(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = /\.(pdf|docx)$/i.test(file.name);
    if (!ok) { toast.error("Ladda upp en PDF- eller DOCX-fil"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Filen är för stor (max 25 MB)"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/cv-import/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const ex = data.extracted;
      const sel = {};
      ["work_experiences", "educations", "skills", "systems_tools"].forEach((k) => {
        if ((ex[k] || []).length) sel[k] = true;
      });
      if (ex.contact && (ex.contact.email || ex.contact.phone || ex.contact.city || ex.full_name)) sel.contact = true;
      setSelected(sel);
      toast.success("CV inläst – granska nedan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const apply = async () => {
    const sections = Object.keys(selected).filter((k) => selected[k]);
    if (!sections.length) { toast.error("Välj minst ett avsnitt att lägga till"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/cv-import/${result.import_id}/apply`, { sections, mode: "complete_only" });
      const total = Object.values(data.added).reduce((a, b) => a + b, 0);
      toast.success(total ? `${total} poster tillagda i din profil` : "Inget nytt att lägga till (dubbletter hoppades över)");
      navigate("/app/profil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setApplying(false); }
  };

  const ex = result?.extracted;

  if (proposal) {
    return (
      <CVProposalDiff
        proposal={proposal}
        mode={buildMode}
        onOpenEditor={() => navigate(`/app/cv/${proposal.id}`)}
        onBack={() => { setProposal(null); setBuildMode(null); }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Ladda upp CV"
        description="Ladda upp ett befintligt CV (PDF eller DOCX). Vi läser in innehållet och visar vad vi hittat innan något läggs till. Originalet bevaras – vi kompletterar bara din Masterprofil med det du godkänner."
      />

      <Card className="rounded-2xl border-border p-5 mb-6 flex items-center gap-3 bg-muted/50">
        <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
        <p className="text-sm text-muted-foreground">Läge: <strong>Komplettera bara</strong>. Vi ändrar aldrig dina befintliga uppgifter – vi lägger bara till nya poster du väljer. Importerade uppgifter märks med källa <em>uppladdat CV</em>.</p>
      </Card>

      {!result && (
        <Card className="rounded-2xl border-dashed border-2 border-border p-12 text-center">
          <Upload className="h-10 w-10 mx-auto text-primary mb-3" />
          <p className="text-muted-foreground mb-4">Dra in eller välj en PDF- eller DOCX-fil (max 25 MB)</p>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="sr-only" onChange={onFile} data-testid="cv-file-input" />
          <Button className="rounded-full min-h-[44px]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-cv-btn">
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Läser in…</> : <><Upload className="h-4 w-4 mr-2" /> Välj fil</>}
          </Button>
        </Card>
      )}

      {ex && (
        <div className="space-y-5 stagger-in">
          <Card className="rounded-2xl border-border p-6">
            <div className="flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-primary" /><h3 className="font-head text-lg font-semibold">Har vi läst ditt CV rätt?</h3></div>
            <div className="grid gap-2 text-sm">
              {ex.full_name && <div><span className="text-muted-foreground">Namn:</span> {ex.full_name}</div>}
              {ex.headline && <div><span className="text-muted-foreground">Rubrik:</span> {ex.headline}</div>}
              {(ex.contact?.email || ex.contact?.phone || ex.contact?.city) && (
                <div><span className="text-muted-foreground">Kontakt:</span> {[ex.contact.email, ex.contact.phone, ex.contact.city].filter(Boolean).join(" · ")}</div>
              )}
              {ex.languages?.length > 0 && <div><span className="text-muted-foreground">Språk:</span> {ex.languages.join(", ")}</div>}
              {ex.certifications?.length > 0 && <div><span className="text-muted-foreground">Certifikat:</span> {ex.certifications.join(", ")}</div>}
            </div>
          </Card>

          <Card className="rounded-2xl border-border p-6">
            <h3 className="font-head text-lg font-semibold mb-1">Välj vad som ska läggas till i din profil</h3>
            <p className="text-sm text-muted-foreground mb-4">Bocka i de avsnitt du vill importera. Dubbletter hoppas över automatiskt.</p>
            <div className="space-y-3">
              {["work_experiences", "educations", "skills", "systems_tools"].map((k) => {
                const count = (ex[k] || []).length;
                if (!count) return null;
                return (
                  <label key={k} className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-muted/50" data-testid={`section-${k}`}>
                    <Checkbox checked={!!selected[k]} onCheckedChange={(v) => setSelected((p) => ({ ...p, [k]: !!v }))} data-testid={`check-${k}`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{SECTION_LABELS[k]}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {k === "work_experiences" && (ex[k] || []).map((e) => e.role || e.employer).filter(Boolean).join(", ")}
                        {k === "educations" && (ex[k] || []).map((e) => e.name).filter(Boolean).join(", ")}
                        {(k === "skills" || k === "systems_tools") && (ex[k] || []).map((s) => s.name).filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <Badge variant="secondary" className="rounded-full">{count}</Badge>
                  </label>
                );
              })}
              {(ex.contact?.email || ex.contact?.phone || ex.contact?.city || ex.full_name) && (
                <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-muted/50" data-testid="section-contact">
                  <Checkbox checked={!!selected.contact} onCheckedChange={(v) => setSelected((p) => ({ ...p, contact: !!v }))} data-testid="check-contact" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Kontaktuppgifter</div>
                    <div className="text-xs text-muted-foreground">Fyller endast tomma fält – skriver aldrig över befintliga</div>
                  </div>
                </label>
              )}
            </div>
          </Card>

          {result.gaps?.length > 0 && (
            <Card className="rounded-2xl border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 p-6">
              <div className="flex items-center gap-2 font-semibold mb-2 text-[hsl(36,60%,32%)]"><AlertCircle className="h-5 w-5" /> Detta saknas i ditt CV</div>
              <p className="text-sm text-muted-foreground mb-2">Vi hittade inte följande. Vill du komplettera i din profil efteråt?</p>
              <div className="flex flex-wrap gap-2">
                {result.gaps.map((g, i) => <Badge key={i} variant="secondary" className="rounded-full">{g}</Badge>)}
              </div>
            </Card>
          )}

          <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] p-6" data-testid="build-cv-card">
            <div className="flex items-center gap-2 mb-1">
              <Wand2 className="h-5 w-5 text-primary" />
              <h3 className="font-head text-lg font-semibold">Skapa ett CV direkt från detta</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Låt AI arbeta med dina <strong>verkliga uppgifter</strong> från det uppladdade CV:t. Du får se original och förslag sida vid sida innan du gör något. Inget hittas på. Du kan bygga om samma CV hur många gånger du vill – det kostar bara en gång.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => buildCv("improve")}
                disabled={building}
                className="text-left rounded-xl border border-border bg-white p-4 hover:border-primary hover:bg-primary/[0.04] transition-colors disabled:opacity-60"
                data-testid="build-improve-btn"
              >
                <div className="flex items-center gap-2 font-semibold text-sm mb-1">
                  {building && buildMode === "improve" ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Wand2 className="h-4 w-4 text-primary" />}
                  Förbättra varsamt
                </div>
                <p className="text-xs text-muted-foreground">Behåller fakta och din ton – gör otydliga formuleringar tydligare. Lägger inte till ny erfarenhet.</p>
              </button>
              <div className="rounded-xl border border-border bg-white p-4">
                <div className="flex items-center gap-2 font-semibold text-sm mb-1">
                  <Target className="h-4 w-4 text-primary" /> Anpassa till tjänst
                </div>
                <p className="text-xs text-muted-foreground mb-2">Prioriterar och formulerar dina verkliga uppgifter mot en annons. Lägger aldrig till nyckelord du inte har stöd för.</p>
                <Textarea
                  value={jobAd}
                  onChange={(e) => setJobAd(e.target.value)}
                  rows={3}
                  placeholder="Klistra in jobbannonsen här…"
                  className="mb-2 text-sm"
                  data-testid="build-jobad-input"
                />
                <Button size="sm" className="rounded-full w-full" onClick={() => buildCv("adapt")} disabled={building} data-testid="build-adapt-btn">
                  {building && buildMode === "adapt" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Target className="h-4 w-4 mr-2" />}
                  Anpassa till tjänsten
                </Button>
              </div>
            </div>
          </Card>

          <div className="flex flex-wrap justify-between gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => { setResult(null); setSelected({}); }}>Ladda upp en annan fil</Button>
            <Button className="rounded-full" onClick={apply} disabled={applying} data-testid="apply-import-btn">
              {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Lägg till i min profil
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
